import React from 'react';
import { Router as ReachRouter, useLocation } from '@reach/router';
import { mergeDeepRight } from 'ramda';
import { useProducts } from '#core/Products';
import PrivateRoute from '#coreComponents/PrivateRoute';
import PublicAndPrivateRoute from '#coreComponents/PublicAndPrivateRoute';
import { PrivateLayout, PublicLayout } from '#coreComponents/Layout';
import Home from './features/core/Home';
import Dashboard from './features/core/Dashboard';
import NotFound from './features/core/NotFound';
import Faqs from '#core/Faqs';
import SdgQuiz from '#core/SdgQuiz';
import { PrivateRouteLayoutProps } from '#coreComponents/PrivateRoute/PrivateRoute';

const Router = (ReachRouter as unknown) as React.ComponentType<any>;

const ScrollTopOnRouteChange: React.FC<{ path: string }> = ({ children }) => {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return <>{children}</>;
};

const PrivateRouteLayout: React.FC<{}> = ({ children }) => (
  <PrivateLayout contentWrapperProps={{ mt: { base: 20, lg: 0 } }}>
    {children}
  </PrivateLayout>
);

const RouterComponent = () => {
  const { products } = useProducts();

  const notFoundRoute = (
    <PublicAndPrivateRoute
      publicLayout={PublicLayout}
      publicComponent={NotFound}
      privateComponent={NotFound}
      privateLayout={PrivateLayout}
      default={true}
      errorBoundaryId="not-found"
      trackingPageGroup="global"
      publicTrackingPageName="404"
      privateTrackingPageName="404"
      publicPageTitle="Not Found"
      privatePageTitle="Not Found"
    />
  );
  // eslint-disable-next-line no-process-env
  const basepath = process.env.PUBLIC_URL;
  return (
    <>
      <Router basepath={basepath}>
        <ScrollTopOnRouteChange path="/">
          {notFoundRoute}

          <PublicAndPrivateRoute
            privateLayout={PrivateRouteLayout}
            privateComponent={Dashboard}
            publicLayout={PublicLayout}
            publicComponent={Home}
            path="/"
            errorBoundaryId="home"
            trackingPageGroup="global"
            publicTrackingPageName="home"
            privateTrackingPageName="dashboard"
            publicPageTitle="Home"
            privatePageTitle="Dashboard"
          />

          <PublicAndPrivateRoute
            privateLayout={PrivateRouteLayout}
            publicLayout={PublicLayout}
            privateComponent={Faqs}
            publicComponent={Faqs}
            path="/faqs"
            errorBoundaryId="faqs"
            trackingPageGroup="global"
            publicTrackingPageName="faqs"
            privateTrackingPageName="faqs"
            publicPageTitle="FAQs"
            privatePageTitle="FAQs"
          />

          <PublicAndPrivateRoute
            privateComponent={SdgQuiz}
            publicComponent={SdgQuiz}
            path="/sdg-quiz"
            errorBoundaryId="sdg-quiz"
            trackingPageGroup="global"
            publicTrackingPageName="sdg-quiz"
            privateTrackingPageName="sdg-quiz"
            publicPageTitle="SDG-QUIZ"
            privatePageTitle="SDG-QUIZ"
          />

          {products.map(({ pageQuery: PageQuery, ...product }) => {
            const pages = product.pages.map((page) => {
              const layoutConfig = mergeDeepRight(
                { ...product.productConfig, parentPath: product.path },
                page.pageConfig || {},
              );

              return (
                <PrivateRoute
                  key={product.path}
                  layout={
                    (page.layout || PrivateRouteLayout) as React.FC<
                      PrivateRouteLayoutProps
                    >
                  }
                  layoutConfig={layoutConfig}
                  component={page.component}
                  path={PageQuery ? page.path : page.routerPath}
                  auth={product.authKey ? page.auth : undefined}
                  errorBoundaryId={product.path}
                  trackingPageGroup={product.path}
                  trackingPageName={page.path}
                  upsellComponent={page.upsellComponent}
                  pageTitle={`${product.name} ${page.name}`}
                />
              );
            });

            if (PageQuery) {
              return (
                <PageQuery key={product.path} path={product.path}>
                  {notFoundRoute}
                  {pages}
                </PageQuery>
              );
            }

            return pages;
          })}
        </ScrollTopOnRouteChange>
      </Router>
    </>
  );
};

export default RouterComponent;
